import { Capacitor } from '@capacitor/core';
import { trackEvent } from './analytics';

/**
 * F2 (2.1.0) — opening a track in Deezer should land in the Deezer APP first,
 * not the mobile web site. On the native shell we try the deezer:// scheme and
 * fall back to the web URL if the app never takes over (not installed).
 * Other services keep the plain web URL (their https links app-link natively).
 */

/** deezer:// mirrors the https URL path, so the mapping is mechanical. */
export function getDeezerAppUrl(webUrl: string): string {
  return webUrl.replace(/^https?:\/\//, 'deezer://');
}

/** App-scheme attempt is Deezer-only and native-only (web tabs keep https). */
export function shouldTryAppScheme(service: string, platform: string): boolean {
  return service === 'deezer' && (platform === 'ios' || platform === 'android');
}

const FALLBACK_DELAY_MS = 1400;

/**
 * Open a streaming-service URL. Deezer on the native shell: navigate to the
 * app scheme, and only if the app didn't take over (page still visible after
 * a short delay) open the web URL. Everything else: plain new-tab open.
 */
export function openServiceUrl(service: string, webUrl: string): void {
  trackEvent('Listen', 'open', service);
  if (!shouldTryAppScheme(service, Capacitor.getPlatform())) {
    window.open(webUrl, '_blank');
    return;
  }

  let fellBack = false;
  const timer = setTimeout(() => {
    if (document.visibilityState === 'visible') {
      fellBack = true;
      window.open(webUrl, '_blank');
    }
  }, FALLBACK_DELAY_MS);

  const onHide = () => {
    // The Deezer app took over — cancel the web fallback.
    if (!fellBack) clearTimeout(timer);
    document.removeEventListener('visibilitychange', onHide);
  };
  document.addEventListener('visibilitychange', onHide);

  window.location.href = getDeezerAppUrl(webUrl);
}
